import type { ButtonProps, InputProps, TextProps } from '@chakra-ui/react'

// The auth screens render the dark RallyPD design at every breakpoint. Mobile
// established this palette; desktop adopted it in HF-1546, replacing the legacy
// light split-panel card. All shared tokens / style props live here so each auth
// page stays consistent — and so `src/constants/dark-tokens.ts`, which re-exports
// several of them, never drifts away from the login screens.

export const AUTH_MOBILE_BG = '#0C1421'

export const AUTH_MOBILE_BUTTON_GRADIENT =
  'linear-gradient(180deg, #2E8FCC 0%, #1F72A6 12.31%, #093E76 102.38%)'

// Slightly brighter version used for hover / active feedback.
export const AUTH_MOBILE_BUTTON_GRADIENT_HOVER =
  'linear-gradient(180deg, #3FA0DD 0%, #2987BC 12.31%, #0E4685 102.38%)'

// Anchor the form ~1/3 from the top of the viewport on mobile so it sits in
// the upper third of the screen, naturally adapting to any screen height.
// Long forms simply scroll past the bottom edge, short forms still feel balanced.
// Desktop centers the form column instead, so this only applies below LG.
export const AUTH_MOBILE_TOP_OFFSET = '12vh'

export const AUTH_MOBILE_ACCENT = '#2E8FCC'

/**
 * var(--Surface-Gradient) — the raised dark surface. Defined here rather than in
 * `src/constants/dark-tokens.ts` so that AuthLayout never has to import from
 * dark-tokens (which imports back out of this file); keeping the dependency
 * one-way rules out a module-init cycle that would resolve tokens to undefined
 * at runtime with no type error. dark-tokens re-exports it for everyone else.
 */
export const DARK_SURFACE_GRADIENT = 'linear-gradient(180deg, #172437 0%, #0E1928 100%)'

export const authTitleColor = 'white'
export const authLabelColor = 'whiteAlpha.900'
export const authMutedColor = 'whiteAlpha.700'
export const authLinkColor = AUTH_MOBILE_ACCENT
export const authBackArrowColor = 'white'

// Hairline rule used by the "or" divider and other separators on the dark surface.
export const AUTH_DIVIDER_COLOR = 'whiteAlpha.300'

// Selectable option cards ("You're a…", "How will you get started?").
export const authOptionCardSelectedBg = DARK_SURFACE_GRADIENT
export const authOptionCardSelectedBorder = '#4DB3FF'
export const authOptionCardBorder = 'rgba(194, 199, 214, 0.30)'
export const authOptionCardBorderHover = 'rgba(194, 199, 214, 0.50)'
// Mid grey with enough contrast against the dark card.
export const AUTH_OPTION_CARD_ICON = '#ADB2BF'

// Resting (un-focused) input border — neutral cool grey at 30% alpha.
export const AUTH_MOBILE_INPUT_BORDER = 'rgba(194, 199, 214, 0.30)'
// Slightly brighter version for hover feedback.
export const AUTH_MOBILE_INPUT_BORDER_HOVER = 'rgba(194, 199, 214, 0.50)'

export const authInputProps: InputProps = {
  borderRadius: '8px',
  borderColor: AUTH_MOBILE_INPUT_BORDER,
  // Keep the input transparent so the dark page bg shows through.
  bg: 'transparent',
  color: 'white',
  h: '48px',
  fontSize: '15px',
  _placeholder: { color: 'whiteAlpha.500' },
  _hover: { borderColor: AUTH_MOBILE_INPUT_BORDER_HOVER },
  _focus: {
    borderColor: AUTH_MOBILE_ACCENT,
    boxShadow: `0 0 0 1px ${AUTH_MOBILE_ACCENT}`
  },
  // Override browser autofill so it doesn't paint a white box over the dark bg.
  sx: {
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
      WebkitBoxShadow: `0 0 0 1000px ${AUTH_MOBILE_BG} inset`,
      WebkitTextFillColor: 'white',
      caretColor: 'white',
      transition: 'background-color 9999s ease-in-out 0s'
    }
  }
}

export const authButtonProps: ButtonProps = {
  bg: AUTH_MOBILE_BUTTON_GRADIENT,
  color: 'white',
  borderRadius: '999px',
  h: '48px',
  fontSize: '16px',
  fontWeight: '600',
  _hover: { bg: AUTH_MOBILE_BUTTON_GRADIENT_HOVER },
  _active: { opacity: 0.92 },
  _focus: { boxShadow: 'none' },
  border: 'none',
  w: '100%'
}

export const authPlaceholderTextProps: TextProps = {
  color: 'whiteAlpha.500'
}

// Style for each PinInputField cell on the verify-email screen.
// Each cell flex-grows to fill the form column with equal share, but caps at
// 56px max so it stays a square on wider screens. Combined with the parent
// HStack `spacing="8px"`, the row scales naturally on any phone width.
// Typed as Omit<InputProps, 'size'> because PinInputField's `size` is the native
// HTML number while Chakra's InputProps `size` is a string variant — keeping
// `size` out of this object lets us spread it onto PinInputField without conflict.
export const authPinFieldProps: Omit<InputProps, 'size'> = {
  borderRadius: '10px',
  borderColor: AUTH_MOBILE_INPUT_BORDER,
  bg: 'transparent',
  color: 'white',
  fontSize: { base: '20px', md: '22px' },
  fontWeight: 600,
  flex: 1,
  minW: 0,
  maxW: '56px',
  h: '56px',
  p: 0,
  textAlign: 'center',
  _hover: { borderColor: AUTH_MOBILE_INPUT_BORDER_HOVER },
  _focus: {
    borderColor: AUTH_MOBILE_ACCENT,
    boxShadow: `0 0 0 1px ${AUTH_MOBILE_ACCENT}`
  }
}

// chakra-react-select surfaces on the auth screens. The library paints its own
// light-themed control, menu and chips, which vanish or clash on the dark
// layout, so each surface is repainted to match the form fields.
export const authSelectChipBg = 'whiteAlpha.200'
export const authSelectMenuBg = '#101B2B'
export const authSelectOptionHoverBg = 'whiteAlpha.200'
